import { useMemo } from 'react';

import {
  useCreateWaypointTemplateUploadImageUrlMutation,
  useRefreshWaypointTemplateImageStatusMutation,
} from '@dashboard/generated/wp/platform';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { TSidebarApiState } from '@wp-block-builder/components/SidebarApiClientContext';

export default function useSidebarApiClient() {
  const [baseCreateImageUpload] = useCreateWaypointTemplateUploadImageUrlMutation();
  const [baseRefreshImageUpload] = useRefreshWaypointTemplateImageStatusMutation();
  const platformId = useCurrentPlatformId();

  return useMemo<TSidebarApiState>(
    () => ({
      platformId,
      createImageUpload: async (file: File) => {
        const { data } = await baseCreateImageUpload({
          variables: { contentLength: file.size, name: file.name, mimeType: file.type },
        });
        if (!data) {
          throw new Error('Failed to create image upload URL');
        }
        const obj = data.createWaypointTemplateUploadImageUrl;
        return {
          signedS3UploadUrl: obj.signedS3UploadUrl!,
          waypointTemplateImageId: obj.id,
        };
      },
      refreshImageUpload: async (waypointTemplateImageId: string) => {
        const { data } = await baseRefreshImageUpload({ variables: { waypointTemplateImageId } });
        if (!data) {
          throw new Error('Failed to refresh image upload');
        }
        const obj = data.refreshWaypointTemplateImageStatus;
        return {
          imageId: obj.id,
          imageUrl: obj.url,
        };
      },
    }),
    [platformId, baseCreateImageUpload, baseRefreshImageUpload]
  );
}
